import type { PackageResponse } from '../../types/packages';
import { formatAed } from '../offers/offerReviewModel';
import type { PackageEditErrors, PackageEditForm } from './packageEditModel';

export interface PackageEditItemsProps {
  pkg: PackageResponse;
  form: PackageEditForm;
  errors: PackageEditErrors;
  disabled: boolean;
  onRemoveCover: () => void;
  onRestoreCover: () => void;
}

/**
 * Services and cover. Services are read-only here (the garage builds them; the request sends them back
 * unchanged). There is no admin cover upload endpoint, so the admin can only keep or remove the cover.
 */
export default function PackageEditItems(props: PackageEditItemsProps) {
  const { pkg, form, errors, disabled, onRemoveCover, onRestoreCover } = props;
  const removed = !!pkg.cover?.storageKey && !form.coverStorageKey;

  return (
    <>
      <section className="admin-card" aria-labelledby="package-edit-items">
        <div className="admin-card-header"><h3 className="admin-card-title" id="package-edit-items">Services ({pkg.items.length})</h3></div>
        <p className="offer-edit__hint">The garage chooses the services and fixed choices. Ask the garage to change them.</p>
        <ul className="package-edit__items" data-testid="package-edit-items">
          {pkg.items.map((item) => (
            <li key={item.offeringId} className="package-edit__item">
              <span className="package-edit__item-name">{item.name}</span>
              <span className="admin-text-muted">
                {[...item.fixedChoices.map((c) => `${c.variationName}: ${c.label}`), ...item.openVariations.map((v) => `customer picks ${v.name}`)]
                  .join(' · ') || 'No options'}
              </span>
              <span dir="ltr">{formatAed(item.listPrice)}</span>
            </li>
          ))}
        </ul>
        {errors.items && <div className="offer-edit__error" role="alert">{errors.items}</div>}
      </section>

      <section className="admin-card" aria-labelledby="package-edit-cover">
        <div className="admin-card-header"><h3 className="admin-card-title" id="package-edit-cover">Cover</h3></div>
        {form.coverStorageKey ? (
          form.coverUrl
            ? <img src={form.coverUrl} alt="Package cover" className="offer-edit__banner-img" />
            : <div className="offer-edit__banner-img offer-edit__banner-img--empty">Cover set</div>
        ) : (
          <div className="offer-edit__banner-img offer-edit__banner-img--empty">No cover</div>
        )}
        <div className="offer-edit__banner-actions">
          {form.coverStorageKey && (
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" disabled={disabled} onClick={onRemoveCover}>
              Remove cover
            </button>
          )}
          {removed && (
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" disabled={disabled} onClick={onRestoreCover}>
              Keep the garage's cover
            </button>
          )}
        </div>
        <p className="offer-edit__hint">Only the garage can upload a new cover. Without one, the app shows the navy card.</p>
        {errors.coverStorageKey && <div className="offer-edit__error" role="alert">{errors.coverStorageKey}</div>}
      </section>
    </>
  );
}
